import { nanoid } from 'nanoid';
import { getDecryptAns } from '../../../../../../../utils/decryptAns';
import {
  LessonMaterial,
  FeedbackQuestion,
  MultipleChoiceQuestion,
  ModuleDraft,
  mapAnswerFormatToFeedbackType
} from '../../../../../../../components/admin3/newProgram/masterclass/types';
import type { FieldSetting } from './types';

export function isFieldSetting(value: unknown): value is FieldSetting {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    'mentee' in (value as object) &&
    'mentor' in (value as object) &&
    'admin' in (value as object)
  );
}

const normalizeRoleValue = (rawValue: unknown) => rawValue === true || rawValue === 'true' || rawValue === 1;

export function normalizeOptionalFieldSetting(value: unknown): FieldSetting {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as Record<string, unknown>;
    const hasMentee = Object.hasOwn(v, 'mentee');
    const hasMentor = Object.hasOwn(v, 'mentor');
    const hasAdmin = Object.hasOwn(v, 'admin');
    const menteeValue = normalizeRoleValue(v.mentee);
    const mentorValue = normalizeRoleValue(v.mentor);
    const adminValue = normalizeRoleValue(v.admin);
    return {
      mentee: hasMentee,
      mentor: hasMentor,
      admin: hasAdmin,
      mandatory: (hasMentee && menteeValue) || (hasMentor && mentorValue) || (hasAdmin && adminValue)
    };
  }
  if (value === true) {
    return { mentee: true, mentor: true, admin: true, mandatory: true };
  }
  return { mentee: false, mentor: false, admin: false, mandatory: false };
}

export function mapTemplateToModules(template: any): ModuleDraft[] {
  const levels = [...(template?.levels || [])].sort((a: any, b: any) => {
    const aKey = a?.level ?? a?.levelIndex ?? a?.levelPosition ?? a?.levelOrder ?? a?.levelId ?? null;
    const bKey = b?.level ?? b?.levelIndex ?? b?.levelPosition ?? b?.levelOrder ?? b?.levelId ?? null;
    if (aKey == null && bKey == null) return 0;
    if (aKey == null) return 1;
    if (bKey == null) return -1;
    return Number(aKey) - Number(bKey);
  });
  return levels.map((level: any, levelIndex: number) => {
    const moduleId = level?._id || level?.levelId || nanoid();
    const tasks = [...(level?.tasks || [])].sort(
      (a: any, b: any) => (a?.taskPosition ?? a?.taskIndex ?? 0) - (b?.taskPosition ?? b?.taskIndex ?? 0)
    );
    const moduleLocalId = nanoid();
    return {
      id: moduleLocalId,
      sourceId: moduleId,
      title: level?.levelName || `Module ${levelIndex + 1}`,
      objective: level?.levelDescription || '',
      lessons: tasks.map((task: any, taskIndex: number) => {
        const lessonId = task?._id || task?.taskId || nanoid();
        const lessonLocalId = nanoid();
        const libraryArticles = Array.isArray(task?.libraryArticles) ? task.libraryArticles : [];
        const materials: LessonMaterial[] = libraryArticles.filter(Boolean).map((articleId: string) => ({
          id: articleId || nanoid(),
          articleId,
          title: '',
          reference: '',
          source: 'library'
        }));
        const feedbackQuestions: FeedbackQuestion[] = (task?.menteeQuestions || []).map(
          (question: any, questionIndex: number) => ({
            id: question?._id || `${task?._id ?? task?.taskName ?? 'feedback'}-${questionIndex}`,
            _id: question?._id,
            prompt: question?.menteeQues || question?.menteeAsmtQues || `Question ${questionIndex + 1}`,
            type: mapAnswerFormatToFeedbackType(question?.menteeAnsType || question?.type)
          })
        );
        const multipleChoiceQuestions: MultipleChoiceQuestion[] = (task?.MCQ || task?.Question || []).map(
          (question: any, questionIndex: number) => {
            const rawOptions: any[] = Array.isArray(question?.options) ? question.options : [];
            const mappedOptions = rawOptions.map((option: any, optionIndex: number) => {
              const label =
                typeof option === 'string'
                  ? option
                  : option?.optionText || option?.label || option?.value || `Option ${optionIndex + 1}`;
              const id =
                (typeof option === 'object' && (option?.id || option?._id || option?.value)) ||
                `${question?._id ?? task?._id ?? 'mcq'}-option-${optionIndex}`;
              return { id, label };
            });
            const normalizedOptions =
              mappedOptions.length > 0
                ? mappedOptions
                : [
                    {
                      id: `${question?._id ?? task?._id ?? 'mcq'}-option-0`,
                      label: 'Option 1'
                    },
                    {
                      id: `${question?._id ?? task?._id ?? 'mcq'}-option-1`,
                      label: 'Option 2'
                    }
                  ];
            const decryptedCorrectAnswer = question?.correctAns ? getDecryptAns(question.correctAns) : '';
            const normalizedDecryptedAnswer = String(decryptedCorrectAnswer || '').trim();
            const correctByCorrectAns = normalizedDecryptedAnswer
              ? normalizedOptions.find((opt: any) => String(opt.label || '').trim() === normalizedDecryptedAnswer)?.id
              : '';
            const ansFlagIndex = rawOptions.findIndex((opt: any) => Boolean(opt?.ans));
            const correctByAnsFlag = ansFlagIndex > -1 ? normalizedOptions[ansFlagIndex]?.id : '';
            const correctOptionId = correctByCorrectAns || correctByAnsFlag || normalizedOptions[0]?.id || '';
            return {
              id: question?._id || `${task?._id ?? 'mcq'}-${questionIndex}`,
              _id: question?._id,
              prompt: question?.ques || `Question ${questionIndex + 1}`,
              options: normalizedOptions,
              correctOptionId,
              optionReasonings: Array.isArray(question?.optionReasonings) ? question.optionReasonings : []
            };
          }
        );
        return {
          id: lessonLocalId,
          sourceId: lessonId,
          title: task?.taskName || `Lesson ${taskIndex + 1}`,
          description: task?.taskDescription || '',
          materials,
          feedbackQuestions: feedbackQuestions.length > 0 ? feedbackQuestions : [],
          multipleChoiceQuestions: multipleChoiceQuestions.length > 0 ? multipleChoiceQuestions : [],
          elliott: task?.elliott ?? null,
          reasoning: task?.reasoning || '',
          learningOutcomesServed: Array.isArray(task?.learningOutcomesServed) ? task.learningOutcomesServed : [],
          ragDocumentTitles: Array.isArray(task?.ragDocumentTitles) ? task.ragDocumentTitles : [],
          capabilityName: task?.capabilityName || null,
          ...(task?.capabilityId || Array.isArray(task?.outcomes) || typeof task?.roleplay === 'boolean'
            ? {
                capabilityId: task?.capabilityId ? String(task.capabilityId) : null,
                outcomes: Array.isArray(task?.outcomes) ? task.outcomes.map(String) : [],
                roleplay: task?.roleplay === true
              }
            : {})
        };
      })
    };
  });
}
